import { Node } from 'prosemirror-model'
import { type EditorState, TextSelection, Transaction } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { isInTable } from 'prosemirror-tables'
import { getNextTextPosition } from '../../utils'
import { isTableCell } from '../../utils/table'

const delectCellContentsBetween = (doc: Node, tr: Transaction, from: number, to: number) => {
  doc.nodesBetween(from, to, (node, pos) => {
    if (isTableCell(node)) {
      const nodeEnd = tr.mapping.map(pos + node.nodeSize)
      // pos = before cell node, + 1 before p + 1 at p start so we just delete text
      const mappedNodePos = tr.mapping.map(pos + 2)
      const mappedFrom = tr.mapping.map(from)
      const mappedTo = tr.mapping.map(to)
      // Entire contents of cell is selected
      if (mappedFrom <= mappedNodePos && nodeEnd > mappedFrom && nodeEnd <= mappedTo) {
        tr.deleteRange(mappedNodePos, nodeEnd)
      } else if (nodeEnd > tr.mapping.map(from) && nodeEnd <= mappedTo) {
        // selection reaches end of cell but not beginning
        tr.deleteRange(mappedFrom, nodeEnd)
      } else if (mappedFrom <= mappedNodePos && nodeEnd > mappedTo) {
        // selection reaches beginning of cell but not end
        tr.deleteRange(mappedNodePos, mappedTo)
      } else {
        // Selection in middle of cell
        tr.deleteRange(mappedFrom, mappedTo)
      }
      return false
    }
    return true
  })
}

export const deleteSelectionWithTable = (state: EditorState, dispatch?: EditorView['dispatch']): boolean => {
  if (isInTable(state)) {
    const { selection, doc, tr } = state
    const { $from, $to } = selection
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      const nodeEndPos = tr.mapping.map(pos + node.nodeSize)
      const nodeStartPos = tr.mapping.map(pos)
      const mappedFrom = tr.mapping.map($from.pos)
      const mappedTo = tr.mapping.map($to.pos)

      // Text selection inside table only goes 4 positions before end of table and doesn't include
      // p end, table_cell end, table_row end, table end = 4
      let comparisonNodeEndPos = node.type === state.schema.nodes.table ? nodeEndPos - 4 : nodeEndPos
      // Selection covers entire node, delete range of node
      if (mappedFrom <= nodeStartPos + 1 && comparisonNodeEndPos >= mappedFrom && comparisonNodeEndPos <= mappedTo) {
        tr.deleteRange(nodeStartPos, nodeEndPos)
        return false
      } else if (nodeEndPos > mappedFrom && comparisonNodeEndPos <= mappedTo) {
        // selection reaches end of node but not beginning
        if (node.type === state.schema.nodes.table) {
          delectCellContentsBetween(doc, tr, $from.pos, pos + node.nodeSize)
        } else {
          tr.deleteRange(mappedFrom, nodeEndPos)
        }
        return false
      } else if (mappedFrom <= nodeStartPos && comparisonNodeEndPos > mappedTo) {
        // selection reaches beginning of node but not end
        if (node.type === state.schema.nodes.table) {
          delectCellContentsBetween(doc, tr, pos, $to.pos)
        } else {
          tr.deleteRange(nodeStartPos, mappedTo)
        }
        return false
      } else {
        // selection in middle of node
        if (node.type === state.schema.nodes.table) {
          delectCellContentsBetween(doc, tr, $from.pos, $to.pos)
        } else {
          tr.deleteRange(mappedFrom, mappedTo)
        }
        return false
      }
    })
    const cursorPos = getNextTextPosition(tr.doc.resolve(tr.mapping.map($from.pos)))
    tr.setSelection(new TextSelection(cursorPos))

    dispatch?.(tr)
    return true
  }
  return false
}
